import React, { useEffect, useState } from "react";
import { Loader2, Zap, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LiabilityCheckbox } from "@/components/LiabilityConsent";
import { CheckoutDialog } from "@/components/CheckoutDialog";

const BOT_ADDONS = /^(exec_secretary|email_bot|full_secretary)$/;
export const monthsFree = (a) => { const n = Number(a?.yearly_price) > 0 ? Math.max(0, Math.round(12 - a.yearly_price / a.price)) : 0; return n === 1 ? "شهر مجانًا" : n === 2 ? "شهران مجانًا" : n > 2 ? `${n} أشهر مجانًا` : ""; };

// نافذة تفعيل إضافة: شهري/سنوي، دفع عبر بوابة الدفع المعتمدة إن كان مفعّلاً، تفعيل فوري للمشرف، وإلا رسالة «قريباً»
export const AddonDialog = ({ addon, onClose, onActivated }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [yearly, setYearly] = useState(false);
  const hasYearly = Number(addon.yearly_price) > 0;
  const isYearly = yearly && hasYearly;
  const price = isYearly ? addon.yearly_price : addon.price;
  const saved = monthsFree(addon);
  const periodLabel = isYearly ? "سنة كاملة" : `${addon.period_days || 30} يوم`;
  useEffect(() => { api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => setPayCfg({ enabled: false })); }, []);

  const go = async () => {
    if (!agreed) return toast.error("يجب الموافقة على إقرار حدود المسؤولية");
    if (payCfg?.enabled) { setCheckout({ kind: "addon", addon: addon.id, yearly: isYearly, amount: price, title: `إضافة ${addon.name} — ${periodLabel}` }); return; }
    if (!isAdmin) return;
    setBusy(true);
    try {
      await api.post(`/addons/${addon.id}/activate`, { accepted_liability: true, yearly: isYearly });
      toast.success(`تم تفعيل ${addon.name} ✅`);
      onActivated?.(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (checkout) return <CheckoutDialog order={checkout} onClose={() => setCheckout(null)} />;
  const soon = payCfg && !payCfg.enabled && !isAdmin;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-popover border-border max-w-md" dir="rtl" data-testid="addon-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-accent" /> تفعيل إضافة {addon.name}</DialogTitle>
          <DialogDescription>{addon.description}</DialogDescription>
        </DialogHeader>
        {hasYearly && (
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-secondary p-1 text-sm" data-testid="addon-billing-toggle">
            <button type="button" onClick={() => setYearly(false)} className={`rounded-lg py-2 font-bold transition-colors ${!yearly ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="addon-billing-monthly">شهري</button>
            <button type="button" onClick={() => setYearly(true)} className={`rounded-lg py-2 font-bold transition-colors ${yearly ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="addon-billing-yearly">
              سنوي {saved && <span className="text-[11px] text-emerald-600 font-bold">— {saved}</span>}
            </button>
          </div>
        )}
        <div className="rounded-xl bg-secondary p-4 flex items-center justify-between">
          <span className="text-sm">قيمة الإضافة ({periodLabel})</span>
          <span className="text-2xl font-extrabold text-primary" data-testid="addon-dialog-price">
            {price} <span className="text-sm">ريال</span>
            {isYearly && <span className="text-xs text-muted-foreground font-normal mr-2">≈ {Math.round(addon.yearly_price / 12)} ريال/شهر</span>}
          </span>
        </div>
        <ul className="space-y-1.5 text-sm">
          {(addon.features || []).map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />{f}</li>)}
        </ul>
        {soon ? (
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-4 text-sm flex items-start gap-2" data-testid="addon-soon-notice">
            <Clock className="w-4 h-4 text-accent mt-0.5 shrink-0" />
            <span>الدفع الإلكتروني للإضافات سيتوفر قريباً. للتفعيل الآن تواصل معنا: <a href="mailto:Support@Rawajsmart.com" className="text-primary hover:underline" dir="ltr">Support@Rawajsmart.com</a></span>
          </div>
        ) : (
          <>
            <LiabilityCheckbox checked={agreed} onChange={setAgreed} />
            {BOT_ADDONS.test(addon.id) && (
              <p className="text-[11px] text-muted-foreground" data-testid="addon-bots-terms">
                بتفعيل خدمة الرد الآلي تفوّض براح الرقمية (<a href="https://brah-tech.com" target="_blank" rel="noreferrer" className="text-primary hover:underline" dir="ltr">BrahTech</a>) بالرد على عملائك باسم نشاطك وفق <a href="/terms" target="_blank" rel="noreferrer" className="text-primary hover:underline">الشروط — بند خدمات الرد الآلي</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="text-primary hover:underline">سياسة الخصوصية</a>.
              </p>
            )}
            <Button onClick={go} disabled={busy || !agreed || !payCfg} className="w-full font-bold gap-2" data-testid="addon-activate-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {payCfg?.enabled ? "ادفع وفعّل الإضافة" : "تفعيل فوري (مشرف)"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
